/* eslint-disable max-len */
import React, {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

import {
  Form,
  Input,
  Button,
  notification,
  Spin,
  Checkbox,
  Select,
} from 'antd';
import * as Yup from 'yup';

import {
  applySarahFoodCompatibility,
  hasSarahFoodSelected,
  isProductDisabledBySarah,
  SARAH_FOOD_COMPATIBILITY_HELP,
} from '../../../helpers/productCompatibility';
import useManager from '../../../hooks/manager';
import api from '../../../services/api';
import Modal from '../../Modal';

const UpdateForm = (_props, ref) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [id, setId] = useState(0);
  const [appIntegrationData, setAppIntegrationData] = useState({});
  const [appIntegrationId, setAppIntegrationId] = useState(null);
  const [token, setToken] = useState('');
  const [company, setCompany] = useState();
  const [active, setActive] = useState(true);
  const [isDev, setIsDev] = useState(false);
  const [useLocalUrl, setUseLocalUrl] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingToken, setLoadingToken] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const { update } = useManager();
  const formRef = useRef();

  const loadProducts = useCallback(async (_appIntegrationId) => {
    if (!_appIntegrationId) {
      setProducts([]);
      return [];
    }
    try {
      const { data } = await api.get(`/product/appintegration/${_appIntegrationId}`);
      setProducts(data);
      return data;
    } catch (err) {
      console.error('Failed to load products', err);
      setProducts([]);
      return [];
    }
  }, []);

  const open = useCallback((openId) => {
    (async () => {
      try {
        setLoading(true);
        setShowModal(true);
        const { data } = await api.get(`/integration/${openId}`);
        console.log(data);
        setId(openId);
        formRef.current.setFieldsValue({ url: data.url });
        formRef.current.setFieldsValue({
          urlAppIntegration: data.urlAppIntegration,
        });
        formRef.current.setFieldsValue({
          tokenAppIntegration: data.tokenAppIntegration,
        });
        formRef.current.setFieldsValue({
          clientIDAppIntegration: data.clientIDAppIntegration,
        });
        formRef.current.setFieldsValue({
          clientSecretAppIntegration: data.clientSecretAppIntegration,
        });
        formRef.current.setFieldsValue({
          userAppIntegration: data.userAppIntegration,
        });
        formRef.current.setFieldsValue({
          passwordAppIntegration: data.passwordAppIntegration,
        });
        setAppIntegrationData(data.appIntegration);
        const integrationId = data.appIntegration?._id || data.appIntegration?.id;
        setAppIntegrationId(integrationId);
        setCompany(data.company._id);
        setToken(data.token);
        setUseLocalUrl(data.useLocalUrl);
        setActive(data.active);
        setIsDev(data.isDev);
        setWebhookUrl(data.webhook_url);

        // Load products and set selected products
        if (integrationId) {
          const loadedProducts = await loadProducts(integrationId);
          if (data.productsIntegration && Array.isArray(data.productsIntegration)) {
            const productIds = data.productsIntegration.map((p) => p.id || p._id);
            setSelectedProducts(
              applySarahFoodCompatibility(productIds, loadedProducts),
            );
          }
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    })();
  }, [formRef, loadProducts]);

  useImperativeHandle(ref, () => ({
    open,
  }));

  const handleGenerateToken = useCallback(() => {
    (async () => {
      try {
        setLoadingToken(true);
        const { data } = await api.get(`/token/${company}`);
        setToken(data);
        setLoadingToken(false);
      } catch (error) {
        setLoadingToken(false);
        notification.error({
          message: 'Falha para gerar token',
        });
      }
    })();
  }, [company]);

  const handleSubmit = useCallback(() => {
    (async () => {
      try {
        const productsIntegration = selectedProducts.map((productId) => {
          const product = products.find((p) => (p._id || p.id) === productId);
          return {
            id: product?._id || product?.id,
            name: product?.name,
            slug: product?.slug,
          };
        });

        const data = {
          ...formRef.current.getFieldsValue(),
          token,
          useLocalUrl: appIntegrationData?.useLocalUrl ? useLocalUrl : false,
          active,
          isDev,
          productsIntegration,
        };

        console.log(data);
        const schema = Yup.object().shape({
          token: Yup.string().required('Gere um token'),
          urlAppIntegration: Yup.string()
            .url('URL inválida')
            .required('Informe a url da integração'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        // validation passed
        update(id, '/integration', data, () => {
          formRef.current.resetFields();
          setValidationErrors({});
          setToken(null);
          setUseLocalUrl(true);
          setActive(true);
          setIsDev(false);
          setCompany(null);
          setSelectedProducts([]);
          setProducts([]);
          setAppIntegrationId(null);
          setShowModal(false);
        });
      } catch (err) {
        console.log(err);
        // Validation failed
        setValidationErrors({});
        // Validation failed
        if (err instanceof Yup.ValidationError) {
          notification.error({
            message: 'Falha na Validação',
          });
          const ve = {};
          err.inner.forEach((e) => {
            ve[e.path] = e.message;
          });
          setValidationErrors(ve);
          return;
        }

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;
          notification.error({
            message,
          });

          return;
        }

        notification.error({
          message: 'Falha ao salvar',
        });
      }
    })();
  }, [update, id, token, active, useLocalUrl, isDev, selectedProducts, products, appIntegrationData]);

  return (
    <Modal
      title="Alterar Integração"
      visible={showModal}
      loading={loading}
      footer={(
        <Button type="primary" size="large" onClick={handleSubmit}>
          Alterar
        </Button>
      )}
      onClose={() => setShowModal(false)}
    >
      <Form ref={formRef} size="large">
        {appIntegrationData?.useLocalUrl && (
          <Form.Item name="useLocalUrl">
            <Checkbox
              checked={useLocalUrl}
              onChange={() => {
                formRef.current.setFieldsValue({ url: '' });
                setUseLocalUrl((prevState) => !prevState);
              }}
            >
              Usar Local URL (Url da rede local do servidor será gerada no start
              do servidor)
            </Checkbox>
          </Form.Item>
        )}
        {appIntegrationData?.useLocalUrl && (
          <Form.Item
            name="url"
            validateStatus={validationErrors.url ? 'error' : ''}
            help={validationErrors.url}
            hasFeedback
          >
            <Input
              placeholder="Url API"
              readOnly={appIntegrationData?.useLocalUrl ? useLocalUrl : false}
            />
          </Form.Item>
        )}
        <Form.Item
          name="urlAppIntegration"
          validateStatus={validationErrors.urlAppIntegration ? 'error' : ''}
          help={validationErrors.urlAppIntegration}
          hasFeedback
        >
          <Input placeholder="Url API da Integração" />
        </Form.Item>
        {appIntegrationData?.useToken && (
          <Form.Item
            name="tokenAppIntegration"
            validateStatus={validationErrors.tokenAppIntegration ? 'error' : ''}
            help={validationErrors.tokenAppIntegration}
            hasFeedback
          >
            <Input placeholder="Token ou Chave da Integração" />
          </Form.Item>
        )}
        {appIntegrationData?.useClientID && (
          <Form.Item
            name="clientIDAppIntegration"
            validateStatus={
              validationErrors.clientIDAppIntegration ? 'error' : ''
            }
            help={validationErrors.clientIDAppIntegration}
            hasFeedback
          >
            <Input placeholder="Cliente ID da Integração" />
          </Form.Item>
        )}
        {appIntegrationData?.useClientSecret && (
          <Form.Item
            name="clientSecretAppIntegration"
            validateStatus={
              validationErrors.clientSecretAppIntegration ? 'error' : ''
            }
            help={validationErrors.clientSecretAppIntegration}
            hasFeedback
          >
            <Input placeholder="Cliente Secret da Integração" />
          </Form.Item>
        )}
        {appIntegrationData?.useUserName && (
          <Form.Item
            name="userAppIntegration"
            validateStatus={validationErrors.userAppIntegration ? 'error' : ''}
            help={validationErrors.userAppIntegration}
            hasFeedback
          >
            <Input placeholder="Usuário da Integração" />
          </Form.Item>
        )}
        {appIntegrationData?.usePassword && (
          <Form.Item
            name="passwordAppIntegration"
            validateStatus={
              validationErrors.passwordAppIntegration ? 'error' : ''
            }
            help={validationErrors.passwordAppIntegration}
            hasFeedback
          >
            <Input placeholder="Senha da Integração" />
          </Form.Item>
        )}
        {appIntegrationId && products.length > 0 && (
          <Form.Item
            validateStatus={validationErrors.productsIntegration ? 'error' : ''}
            help={
              validationErrors.productsIntegration
              || (hasSarahFoodSelected(selectedProducts, products)
                ? SARAH_FOOD_COMPATIBILITY_HELP
                : undefined)
            }
            hasFeedback
          >
            <Select
              mode="multiple"
              placeholder="Selecione os produtos"
              value={selectedProducts}
              onChange={(nextSelected) => {
                setSelectedProducts(
                  applySarahFoodCompatibility(nextSelected, products),
                );
              }}
              optionLabelProp="label"
            >
              {products.map((product) => (
                <Select.Option
                  key={product._id || product.id}
                  value={product._id || product.id}
                  label={product.name}
                  title={product.description}
                  disabled={isProductDisabledBySarah(
                    product,
                    selectedProducts,
                    products,
                  )}
                >
                  {product.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Spin spinning={loadingToken}>
          <Form.Item
            validateStatus={validationErrors.token ? 'error' : ''}
            help={validationErrors.token}
            hasFeedback
          >
            <Input
              value={token}
              placeholder="Token"
              addonAfter={(
                <Button
                  type="primary"
                  size="middle"
                  onClick={handleGenerateToken}
                >
                  Gerar Token
                </Button>
              )}
              readOnly
            />
          </Form.Item>
        </Spin>
        <Form.Item name="active">
          <Checkbox
            checked={active}
            onChange={() => setActive((prevState) => !prevState)}
          >
            Ativo
          </Checkbox>
        </Form.Item>
        <Form.Item name="isDev">
          <Checkbox
            checked={isDev}
            onChange={() => setIsDev((prevState) => !prevState)}
          >
            É uma integração de Teste
          </Checkbox>
        </Form.Item>
        <strong>Web Hook URL: </strong>
        <br />
        {webhookUrl}
      </Form>
    </Modal>
  );
};

export default forwardRef(UpdateForm);
